// jobCountの中からフラグごとの配列が返ってくる
// 年収は別で取得しており、queryParamsを更新するようになっているため、問題なし

import {
  JobCountDetailResponse,
  JobCountResponse,
} from '@doda/api-client/aspida/@types';
import { createOpParams } from '../apiRequest/createOpParams';
import { AppQueryParams } from '../../../constants/queryParams';
import {
  LABEL_FLAG,
  UPPER_AND_LOWER_HA,
} from '../../../constants/seo/jobTrendConstants';

/**
 * 条件項目名と求人数
 */
export interface JobCountType {
  name: string;
  count: number;
}

/**
 * 雇用形態、設立、平均年齢において掛け合わせが発生しないようにjobCountのどちらを使うかの判定処理
 * @param {AppQueryParams} queryParams そのページのクエリパラメータ
 * @param {JobCountResponse} jobCount そのページの掛け合わせを行っている求人件数オブジェクト（ファセット）
 * @param {JobCountResponse | null} jobCountNothingParams 何も掛け合わせを行っていない求人件数オブジェクト
 * @returns 求人件数オブジェクト
 */
export const createArrayForFlag = (
  queryParams: AppQueryParams,
  jobCount: JobCountResponse,
  jobCountNothingParams: JobCountResponse | null
) => {
  // /return用の変数
  let resultJobCount: JobCountResponse = jobCount;
  if (!jobCountNothingParams) {
    return resultJobCount;
  }
  if (queryParams.op) {
    resultJobCount = jobCount;
    const hydrogenOp = createOpParams(queryParams.op);
    if (hydrogenOp.employmentStatus) {
      resultJobCount.employmentStatusList =
        jobCountNothingParams.employmentStatusList;
    } else if (hydrogenOp.employeeAverageAge) {
      resultJobCount.employeeAverageAgeList =
        jobCountNothingParams.employeeAverageAgeList;
    }
  } else if (queryParams.es) {
    resultJobCount.establishList = jobCountNothingParams.establishList;
  }

  return resultJobCount;
};

/**
 * 掛け合わせ考慮とソートを行い、カテゴリごとdataとdatalabel作成処理
 * @param {AppQueryParams}queryParams そのページのクエリパラメータ
 * @param {JobCountDetailResponse[] | undefined}jobCount 求人件数オブジェクト
 * @param {Map<string, string>}kodawariMap カテゴリごとのこだわり条件の項目名の連想配列
 * @param {string}flg カテゴリを判定するフラグ
 * @returns 棒グラフ用に整形された求人件数配列とこだわり項目名配列
 */
export const createArrayForBarGraph = (
  queryParams: AppQueryParams,
  jobCount: JobCountDetailResponse[] | undefined,
  kodawariMap: Map<string, string>,
  flg: string
) => {
  if (!jobCount) {
    return [];
  }
  let updateJobCount = jobCount;
  const newCountList: JobCountType[] = [];

  const hydrogenOp = queryParams.op
    ? createOpParams(queryParams.op)
    : undefined;
  switch (flg) {
    case LABEL_FLAG.REQUIREMENT:
      if (hydrogenOp?.requirements) {
        updateJobCount = jobCount.filter(
          (item) => item.id !== String(hydrogenOp.requirements)
        );
      }
      break;
    case LABEL_FLAG.WORKPLACE_ENVIRONMENT:
      if (hydrogenOp?.workplaceEnvironment) {
        updateJobCount = jobCount.filter(
          (item) => item.id !== String(hydrogenOp.workplaceEnvironment)
        );
      }
      break;
    case LABEL_FLAG.HOLIDAY_AND_WAY_OF_WORKING:
      if (hydrogenOp?.holidayAndWayOfWorking) {
        updateJobCount = jobCount.filter(
          (item) => item.id !== String(hydrogenOp.holidayAndWayOfWorking)
        );
      }
      break;
    case LABEL_FLAG.WELFARE_BENEFITS:
      if (hydrogenOp?.welfareBenefits) {
        updateJobCount = jobCount.filter(
          (item) => item.id !== String(hydrogenOp.welfareBenefits)
        );
      }
      break;
    case LABEL_FLAG.EMPLOYEE_AVERAGE_AGE:
      // 掛け合わせ削除済み
      updateJobCount.sort((a, b) => Number(a.id) - Number(b.id));
      break;
    case LABEL_FLAG.EMPLOYMMENT_STATUS:
      // 掛け合わせ削除済み
      break;
    case LABEL_FLAG.ESTABLISH:
      // 掛け合わせ削除済み
      updateJobCount.sort((a, b) => Number(a.id) - Number(b.id));
      break;
    default:
      break;
  }

  if (flg === LABEL_FLAG.ESTABLISH) {
    kodawariMap.forEach((value, key) => {
      let countVar;
      if (updateJobCount) {
        countVar = updateJobCount.find((list) => list.id === String(key));
      }
      countVar = countVar ? countVar.count : 0;
      return newCountList.push({
        name: value,
        count: countVar,
      });
    });
  } else {
    updateJobCount.forEach((countList) => {
      return newCountList.push({
        name: String(kodawariMap.get(countList.id)),
        count: countList.count,
      });
    });
  }

  if (flg !== LABEL_FLAG.EMPLOYEE_AVERAGE_AGE && flg !== LABEL_FLAG.ESTABLISH) {
    newCountList.sort((a, b) => b.count - a.count);
  }
  const data: number[] = [];
  const label: string[] = [];
  newCountList.forEach((list) => {
    label.push(list.name);
    data.push(list.count);
    return [data, label];
  });
  return [data, label];
};

/**
 * 仕事内容・言語用,カテゴリごとdataとdatalabel作成処理
 * @param {AppQueryParams} queryParams そのページのクエリパラメータ
 * @param {JobCountResponse}jobCountLanguage 言語の求人件数オブジェクト（ファセット）
 * @param {JobCountResponse}jobCountPositions 仕事内容の求人件数オブジェクト（ファセット）
 * @param {Map<string, string>}languageLabel 言語のこだわり条件の項目名の連想配列
 * @param {Map<string, string>}positionsLabel 仕事内容のこだわり条件の項目名の連想配列
 * @returns 棒グラフ用に整形された求人件数配列とこだわり項目名配列
 */
export const createArrayLanguageAndPositionsForBarGraph = (
  queryParams: AppQueryParams,
  jobCountLanguage: JobCountDetailResponse[] | undefined,
  jobCountPositions: JobCountDetailResponse[] | undefined,
  languageLabel: Map<string, string>,
  positionsLabel: Map<string, string>
) => {
  if (!jobCountLanguage || !jobCountPositions) {
    return [];
  }
  let updateJobCountLanguage = jobCountLanguage;
  let updateJobCountPositions = jobCountPositions;
  const hydrogenOp = queryParams.op
    ? createOpParams(queryParams.op)
    : undefined;
  if (hydrogenOp?.languageSkills) {
    updateJobCountLanguage = jobCountLanguage.filter(
      (item) => item.id !== String(hydrogenOp.languageSkills)
    );
  }
  if (hydrogenOp?.positions) {
    updateJobCountPositions = jobCountPositions.filter(
      (item) => item.id !== String(hydrogenOp.positions)
    );
  }

  const newCountList: JobCountType[] = [];
  updateJobCountLanguage.forEach((countList) => {
    return newCountList.push({
      name: String(languageLabel.get(countList.id)),
      count: countList.count,
    });
  });
  updateJobCountPositions.forEach((countList) => {
    return newCountList.push({
      name: String(positionsLabel.get(countList.id)),
      count: countList.count,
    });
  });
  newCountList.sort((a, b) => b.count - a.count);
  const data: number[] = [];
  const label: string[] = [];
  newCountList.forEach((list) => {
    label.push(list.name);
    data.push(list.count);
    return [data, label];
  });
  return [data, label];
};

/**
 * 年収用配列作成
 * @param incomeCountList 年収各求人数
 * @returns 棒グラフ用に整形された求人件数配列とこだわり項目名配列
 */
export const createArrayIncomeForBarGraph = (
  incomeCountList: { id: string; count: number | undefined }[]
) => {
  const newCountList: JobCountType[] = [];
  incomeCountList.sort((a, b) => Number(a.id) - Number(b.id));
  incomeCountList.forEach((countList) => {
    const name = UPPER_AND_LOWER_HA.find((ha) => ha.id === countList.id)?.name;
    if (name) {
      return newCountList.push({ name, count: Number(countList.count) });
    }
    return null;
  });
  const data: number[] = [];
  const label: string[] = [];
  newCountList.forEach((list) => {
    label.push(list.name);
    data.push(list.count);
    return [data, label];
  });
  return [data, label];
};

/**
 * 文字列を指定の長さで区切る処理
 * @param splitString 区切りたい文字列
 * @param length 区切る長さ
 * @returns 区切られた文字列の配列
 */
export const splitByLength = (splitString: string, length: number) => {
  const array: string[] = [];
  if (!splitString || !length || length < 1) {
    return array;
  }
  let index = 0;
  let start = index;
  let end = start + length;
  while (start < splitString.length) {
    array[index] = splitString.substring(start, end);
    index += 1;
    start = end;
    end = start + length;
  }
  return array;
};
